import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, AlertTriangle } from 'lucide-react';
import { Payment, formatCurrency } from '@/lib/payments-service';

interface CancelPaymentDialogProps {
  payment: Payment | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPaymentCancelled: () => void;
}

const CancelPaymentDialog = ({
  payment,
  open,
  onOpenChange,
  onPaymentCancelled,
}: CancelPaymentDialogProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [updateAppointment, setUpdateAppointment] = useState(true);

  const handleCancel = async () => {
    if (!payment) return;

    setLoading(true);
    try {
      // Build the payment notes with cancellation reason
      const currentNotes = payment.payment_notes || '';
      const cancellationNote = `[VOIDED] ${new Date().toISOString()}${cancelReason ? `: ${cancelReason}` : ''}`;
      const newNotes = currentNotes ? `${currentNotes}\n\n${cancellationNote}` : cancellationNote;

      // Update the booking with cancelled payment status
      const updateData: any = {
        payment_status: 'cancelled',
        payment_notes: newNotes,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('bookings')
        .update(updateData)
        .eq('id', payment.id);

      if (error) throw error;

      toast({
        title: 'Payment Cancelled',
        description: `Payment of ${formatCurrency(payment.payment_amount, payment.payment_currency || 'USD')} has been voided`,
      });

      setCancelReason('');
      setUpdateAppointment(true);
      onOpenChange(false);
      onPaymentCancelled();
    } catch (error) {
      console.error('Error cancelling payment:', error);
      toast({
        title: 'Error',
        description: 'Failed to cancel payment. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!payment) return null;

  const isStripePayment = !!payment.stripe_session_id;
  const canCancel = payment.payment_status === 'pending' || 
                    payment.payment_source === 'offline' ||
                    !isStripePayment;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Cancel / Void Payment
          </AlertDialogTitle>
          <AlertDialogDescription>
            {isStripePayment && payment.payment_status !== 'pending' ? (
              <span className="text-destructive">
                This is a completed Stripe payment. To refund it, please use the Stripe dashboard. 
                You can mark it as cancelled here for internal tracking.
              </span>
            ) : (
              'Are you sure you want to void this payment? This will mark it as cancelled in the system.'
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="py-4 space-y-4">
          {/* Payment Summary */}
          <div className="p-3 bg-muted rounded-lg">
            <div className="flex justify-between items-start mb-2">
              <div>
                <p className="font-medium">{payment.full_name}</p>
                <p className="text-sm text-muted-foreground">{payment.email}</p>
              </div>
              <div className="text-right">
                <p className="font-bold text-lg">
                  {formatCurrency(payment.payment_amount, payment.payment_currency || 'USD')}
                </p>
                <p className="text-xs text-muted-foreground capitalize">
                  {payment.payment_source === 'offline' ? 'Offline' : 'Online'} • {payment.payment_method || 'N/A'}
                </p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">{payment.service}</p>
          </div>

          {/* Cancel Reason */}
          <div className="space-y-2">
            <Label htmlFor="cancelReason">Reason for cancellation (optional)</Label>
            <Textarea
              id="cancelReason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Enter the reason for voiding this payment..."
              rows={3}
            />
          </div>

          {/* Option to update appointment status */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="updateAppointment"
              checked={updateAppointment}
              onCheckedChange={(checked) => setUpdateAppointment(checked as boolean)}
            />
            <Label htmlFor="updateAppointment" className="text-sm">
              Also update the appointment status to cancelled
            </Label>
          </div>

          {isStripePayment && payment.payment_status !== 'pending' && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> This will only mark the payment as cancelled in our system. 
                For actual refunds, please process them through the Stripe dashboard.
              </p>
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Keep Payment
          </Button>
          <Button
            variant="destructive"
            onClick={handleCancel}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              'Yes, Void Payment'
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default CancelPaymentDialog;
